import { faTriangleExclamation } from "@fortawesome/pro-regular-svg-icons";
import {
    faCheck,
    faCircleNodes,
    faExclamationCircle,
    faMultiply,
    faRefresh,
    faServer,
    faSpinner,
    faTimes,
    faUserLock,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ITable, ITableRow } from "userful-chronos-app-common-js/dist/models/table/userfulTableInterface";
import BackupActions from "../../../components/backupAndRestore/BackupGroupActions";
import UserGroupActions from "../../../components/userGroups/userGroupActions/UserGroupActions";
import UserActions from "../../../components/users/userActions/UserActions";
import {
    BackendCaptureCardInfo,
    BackendHardwareInfo,
    BackupData,
    ClusterDeviceInfo,
    GpuInfo,
    IClusterInfo,
    KeycloakGroup,
    KeycloakUser,
    MemoryInfo,
} from "../../../model/CarbonExternalModels";
import { AppSliceActions } from "../../../store/app/appSlice";
import carbonStore from "../../../store/carbonStore";
import { formatCPU, formatGPU, formatMemory, formatStorage, formatTimestamp } from "../../functions/formatters";

export const convertKeycloakUserToTable = (users: KeycloakUser[], groups: KeycloakGroup[]): ITable => {
    const table: ITable = { id: "users", headers: [], rows: [] };

    // Enter Headers
    table.headers = [
        { id: "users", component: <>Username</> },
        { id: "firstName", component: <>First Name</> },
        { id: "lastName", component: <>Last Name</> },
        { id: "email", component: <>Email Address</> },
        { id: "status", component: <>Status</> },
        { id: "permissions", component: <>Permission Sets</> },
    ];

    for (let i = 0; i < users.length; i++) {
        const user: KeycloakUser = users[i];

        const row: ITableRow = {
            id: user.id.value,
            columns: [
                {
                    id: "users",
                    component: (
                        <div className="cluster normalGap">
                            <div className="circleTag">{user.userProfile.userName[0].toUpperCase()}</div>
                            <p>{user.userProfile.userName}</p>
                        </div>
                    ),
                },
                {
                    id: "firstName",
                    component: <p>{user.userProfile.firstName ? user.userProfile.firstName : ""}</p>,
                },
                {
                    id: "lastName",
                    component: <p>{user.userProfile.lastName ? user.userProfile.lastName : ""}</p>,
                },
                {
                    id: "email",
                    component: <p>{user.userProfile.email}</p>,
                },
                {
                    id: "status",
                    component: (
                        <div>
                            {user.userProfile.enabled ? (
                                <div className="green normal-font">
                                    <FontAwesomeIcon icon={faCheck} /> Active
                                </div>
                            ) : (
                                <div className="red normal-font">
                                    <FontAwesomeIcon icon={faTimes} /> Inactive
                                </div>
                            )}
                        </div>
                    ),
                },
                {
                    id: "permissions",
                    component: (
                        <div className="tagNameContainer">
                            {user.groups.map((g, index) => {
                                const group = groups.find((group) => group.id.value === g.value);

                                if (!group) return null;

                                return (
                                    <div
                                        className="tagNameObject"
                                        onClick={(e) => {
                                            e.stopPropagation();

                                            carbonStore.dispatch(
                                                AppSliceActions.setAppView({ type: "USER_GROUP", id: g }),
                                            );
                                        }}
                                    >
                                        {group.name}
                                    </div>
                                );
                            })}
                        </div>
                    ),
                },
            ],
            actionsComponents: [<UserActions user={user} />],
            isDraggable: false,
        };
        table.rows.push(row);
    }

    return table;
};

export const convertKeycloakGroupToTable = (groups: KeycloakGroup[], users: KeycloakUser[]): ITable => {
    const table: ITable = { id: "group", headers: [], rows: [] };

    table.headers = [
        { id: "name", component: <>Name</> },
        { id: "description", component: <>Description</> },
        { id: "assignedUsers", component: <>Assigned Users</> },
    ];

    for (let i = 0; i < groups.length; i++) {
        const group: KeycloakGroup = groups[i];

        const row: ITableRow = {
            id: group.id.value,
            columns: [
                {
                    id: "name",
                    component: (
                        <div className="cluster normalGap limit">
                            <FontAwesomeIcon className="icon blue" icon={faUserLock} />
                            <p>{group.name}</p>
                        </div>
                    ),
                },
                {
                    id: "description",
                    component: <p>{group.description}</p>,
                },
                {
                    id: "assignedUsers",
                    component:
                        group.members.length > 0 ? (
                            <div className="tagNameContainer">
                                {group.members.map((m, index) => {
                                    const member = users.find((u) => u.id.value === m.value);

                                    if (!member) return null;

                                    return (
                                        <div key={m.value} className="tagNameObject tagNameObjectNonInteractive">
                                            {member.userProfile.userName}
                                        </div>
                                    );
                                })}
                            </div>
                        ) : null,
                },
            ],
            actionsComponents: [<UserGroupActions group={group} />],
        };
        table.rows.push(row);
    }

    return table;
};

export const getCleanNodeValue = (clusterDeviceInfo: ClusterDeviceInfo[]): number => {
    let totalNodes = 0;
    for (let i = 0; i < clusterDeviceInfo.length; i++) {
        if (clusterDeviceInfo[i].active === true) {
            totalNodes += 1;
        }
    }
    return totalNodes;
};

export const convertIClusterInfoToTable = (clusters: IClusterInfo[]): ITable => {
    const table: ITable = { id: "clusterInfo", headers: [], rows: [] };

    table.headers = [
        { id: "name", component: <>Name</> },
        { id: "ipAddress", component: <>IP Address</> },
        { id: "version", component: <>Version</> },
        { id: "status", component: <>Status</> },
        { id: "failoverStatus", component: <>Failover Status</> },
        { id: "licenseStatus", component: <>License Status</> },
        { id: "mappingGroups", component: <>Mapping Groups</> },
        { id: "nodes", component: <>Nodes</> },
        { id: "endpoints", component: <>End-Points</> },
        { id: "activeSources", component: <>Active Sources</> },
        { id: "activeDestinations", component: <>Active Destinations</> },
    ];

    for (let i = 0; i < clusters.length; i++) {
        const cluster: IClusterInfo = clusters[i];

        let totalUClients = 0;

        const row: ITableRow = {
            id: cluster.id.value,
            columns: [
                {
                    id: "name",
                    component: (
                        <div className="cluster normalGap limit">
                            <FontAwesomeIcon className="icon blue" icon={faCircleNodes} />
                            <p>{cluster.name}</p>
                        </div>
                    ),
                },
                {
                    id: "ipAddress",
                    component: <p>{cluster.ip}</p>,
                },
                {
                    id: "version",
                    component: <p>Userful {cluster.version.length >= 1 ? cluster.version : "X.X"}</p>,
                },
                {
                    id: "status",
                    component: (
                        <div>
                            {cluster.status === "CONNECTED" && (
                                <div className="cluster normalGap limit">
                                    <FontAwesomeIcon className="green" icon={faCheck} />
                                    <p>Online</p>
                                </div>
                            )}
                            {cluster.status === "DISCONNECTED" && (
                                <div className="cluster normalGap limit">
                                    <FontAwesomeIcon className="red" icon={faMultiply} />
                                    <p>Offline</p>
                                </div>
                            )}
                        </div>
                    ),
                },
                {
                    id: "failoverStatus",
                    component: (
                        <div>
                            {cluster.license.failOver && <FontAwesomeIcon className="green" icon={faCheck} />}
                            {!cluster.license.failOver && <FontAwesomeIcon className="red" icon={faMultiply} />}
                        </div>
                    ),
                },
                {
                    id: "licenseStatus",
                    component: (
                        <div>
                            {cluster.license.active && <FontAwesomeIcon className="green" icon={faCheck} />}
                            {!cluster.license.active && <FontAwesomeIcon className="red" icon={faMultiply} />}
                        </div>
                    ),
                },
                {
                    id: "mappingGroups",
                    component: <p>{cluster.mappingGroups}</p>,
                },
                {
                    id: "nodes",
                    component: <p>{getCleanNodeValue(cluster.clusterDeviceInfo)}</p>,
                },
                {
                    id: "endpoints",
                    component: <p>{totalUClients}</p>,
                },
                {
                    id: "activeSources",
                    component: <p>{cluster.activeSources}</p>,
                },
                {
                    id: "activeDestinations",
                    component: <p>{cluster.activeDestinations}</p>,
                },
            ],
            actionsComponents: [<div></div>],
            isDraggable: false,
        };
        table.rows.push(row);
    }

    return table;
};

export const convertIBackupAndRestoreToTable = (backupAndRestores: BackupData[]): ITable => {
    const table: ITable = { id: "backupAndRestore", headers: [], rows: [] };

    table.headers = [
        { id: "name", component: <>Name</> },
        { id: "backupStatus", component: <>Backup Status</> },
        { id: "clusterName", component: <>Cluster Name</> },
        { id: "clusterType", component: <>Cluster Type</> },
        { id: "backupType", component: <>Backup Type</> },
    ];

    for (let i = 0; i < backupAndRestores.length; i++) {
        const backupAndRestore: BackupData = backupAndRestores[i];

        const row: ITableRow = {
            id: backupAndRestore.id.value,
            columns: [
                {
                    id: "name",
                    component: (
                        <div className="table-row-column">
                            <FontAwesomeIcon icon={faRefresh} />
                            <p>
                                {backupAndRestore.metadata ? backupAndRestore.metadata.name : backupAndRestore.id.value}
                            </p>
                        </div>
                    ),
                },
                {
                    id: "backupStatus",
                    component: (
                        <div className="table-row-column">
                            {backupAndRestore.status === "IN_PROGRESS" && (
                                <FontAwesomeIcon className="spin-animation blue" icon={faSpinner} />
                            )}
                            {backupAndRestore.status === "COMPLETED" && (
                                <FontAwesomeIcon className="green" icon={faCheck} />
                            )}
                            {backupAndRestore.status === "ERROR" && (
                                <FontAwesomeIcon className="red" icon={faExclamationCircle} />
                            )}
                            {backupAndRestore.status === "UNEXPECTED_PROCESSING_DELAY" && (
                                <FontAwesomeIcon className="yellow" icon={faTriangleExclamation} />
                            )}
                            <p>
                                {backupAndRestore.status === "IN_PROGRESS" ? "Generating Backup" : null}
                                {backupAndRestore.status === "COMPLETED" && backupAndRestore.metadata
                                    ? formatTimestamp(backupAndRestore.metadata.date)
                                    : null}
                                {backupAndRestore.status === "ERROR" ? "Error Occurred" : null}
                                {backupAndRestore.status === "UNEXPECTED_PROCESSING_DELAY"
                                    ? "Excessive Wait Time"
                                    : null}
                            </p>
                        </div>
                    ),
                },
                {
                    id: "clusterName",
                    component: <p>{backupAndRestore.metadata ? backupAndRestore.metadata.clusterName : "-"}</p>,
                },
                {
                    id: "clusterType",
                    component: (
                        <p>
                            {backupAndRestore.metadata
                                ? backupAndRestore.metadata.clusterType === "AETHER_MANAGER"
                                    ? "Aether Manager"
                                    : backupAndRestore.metadata.clusterType
                                : "-"}
                        </p>
                    ),
                },
                {
                    id: "backupType",
                    component: (
                        <p>
                            {backupAndRestore.metadata
                                ? backupAndRestore.metadata.backupType === "MANUAL"
                                    ? "Manual"
                                    : backupAndRestore.metadata.backupType
                                : "-"}
                        </p>
                    ),
                },
            ],
            actionsComponents: [
                <BackupActions backup={backupAndRestore} disabled={backupAndRestore.status === "IN_PROGRESS"} />,
            ],
            isDraggable: false,
        };
        table.rows.push(row);
    }

    return table;
};

export const convertClusterDeviceInfoToTable = (devices: ClusterDeviceInfo[]): ITable => {
    const table: ITable = { id: "devicesInfo", headers: [], rows: [] };

    table.headers = [
        { id: "name", component: <>Name</> },
        { id: "endpoints", component: <>End-Points</> },
        { id: "ram", component: <>RAM</> },
        { id: "gpu", component: <>GPU</> },
        { id: "cpu", component: <>CPU</> },
        { id: "storage", component: <>Storage</> },
    ];

    for (let i = 0; i < devices.length; i++) {
        const device: ClusterDeviceInfo = devices[i];

        const row: ITableRow = {
            id: device.id.value,
            columns: [
                {
                    id: "name",
                    component: (
                        <div className="cluster normalGap limit">
                            <FontAwesomeIcon className="icon blue" icon={faServer} />
                            <p>{device.name}</p>
                        </div>
                    ),
                },
                {
                    id: "endpoints",
                    component: (
                        <>
                            <div className="greenBadge">
                                <p>{device.endpoint.online} Online</p>
                            </div>
                            <div className="redBadge">
                                <p>{device.endpoint.offline} Offline</p>
                            </div>
                        </>
                    ),
                },
                {
                    id: "ram",
                    component: <p>{formatMemory(device.deviceUsage.memory)}</p>,
                },
                {
                    id: "gpu",
                    component: <p>{formatGPU(device.deviceUsage.gpu)}</p>,
                },
                {
                    id: "cpu",
                    component: <p>{formatCPU(device.deviceUsage.cpu)}</p>,
                },
                {
                    id: "storage",
                    component: <p>{formatStorage(device.deviceUsage.storage)}</p>,
                },
            ],
            actionsComponents: [<div></div>],
            isDraggable: false,
        };
        if (device.active) table.rows.push(row);
    }

    return table;
};

export const convertBackendHardwareInfoToTable = (hardwareInfo: BackendHardwareInfo): ITable => {
    const table: ITable = { id: "backendHardware", headers: [], rows: [] };

    table.headers = [
        { id: "summary", component: <>Summary</>, width: "20%", minWidth: 100 },
        { id: "value", component: "", width: "80%", minWidth: 100 },
    ];

    const summaryRows: { id: string; label: string; value: React.ReactNode }[] = [
        { id: "manufacturer", label: "Manufacturer", value: hardwareInfo.systemInfo.manufacturer },
        { id: "productName", label: "Product Name", value: hardwareInfo.systemInfo.productName },
        { id: "serialNumber", label: "Serial Number", value: hardwareInfo.systemInfo.serialNumber },
        { id: "family", label: "Family", value: hardwareInfo.systemInfo.family },
        { id: "systemId", label: "System ID", value: hardwareInfo.systemInfo.systemID },
        { id: "skuNumber", label: "SKU Number", value: hardwareInfo.systemInfo.skuNumber },
        { id: "maxCapacity", label: "Maximum Capacity", value: hardwareInfo.memoryInfoList.maxCapacity },
        { id: "memorySlots", label: "Memory Slots", value: hardwareInfo.memoryInfoList.numberOfDevices },
    ];

    for (const row of summaryRows) {
        table.rows.push({
            id: row.id,
            columns: [
                {
                    id: "summary",
                    component: (
                        <div className="cluster normalGap limit">
                            <p>{row.label}</p>
                        </div>
                    ),
                },
                {
                    id: "value",
                    component: (
                        <div className="cluster normalGap limit">
                            <p>{row.value}</p>
                        </div>
                    ),
                },
            ],
            actionsComponents: [<></>],
            isDraggable: false,
        });
    }

    return table;
};

export const convertGPUToTable = (gpu: GpuInfo[]): ITable => {
    const table: ITable = { id: "gpu", headers: [], rows: [] };

    table.headers = [
        { id: "model", component: <>Model</>, width: "30%", minWidth: 100 },
        { id: "type", component: <>Type</>, width: "35%", minWidth: 100 },
        { id: "slotId", component: <>Slot ID</>, width: "35%", minWidth: 100 },
    ];

    for (let i = 0; i < gpu.length; i++) {
        const device: GpuInfo = gpu[i];

        const row: ITableRow = {
            id: device.driver,
            columns: [
                {
                    id: "model",
                    component: <p>{device.model}</p>,
                },
                {
                    id: "type",
                    component: <p>{device.type}</p>,
                },
                {
                    id: "slotId",
                    component: <p>{device.slotID}</p>,
                },
            ],
            actionsComponents: [<></>],
            isDraggable: false,
        };
        table.rows.push(row);
    }

    return table;
};

export const convertMemoryInfoToTable = (hardwareInfo: BackendHardwareInfo): ITable => {
    const table: ITable = { id: "memoryInfo", headers: [], rows: [] };

    table.headers = [
        { id: "manufacturer", component: <>Manufacturer</>, width: "25%", minWidth: 100 },
        { id: "size", component: <>Size</>, width: "25%", minWidth: 100 },
        { id: "serialNumber", component: <>Serial Number</>, width: "25%", minWidth: 100 },
        { id: "speed", component: <>Speed</>, width: "25%", minWidth: 100 },
    ];

    for (let i = 0; i < hardwareInfo.memoryInfoList.memoryList.length; i++) {
        const memory: MemoryInfo = hardwareInfo.memoryInfoList.memoryList[i];

        const row: ITableRow = {
            id: i + "",
            columns: [
                {
                    id: "manufacturer",
                    component: (
                        <div className="cluster normalGap limit">
                            <p>{memory.manufacturer}</p>
                        </div>
                    ),
                },
                {
                    id: "size",
                    component: (
                        <div className="cluster normalGap limit">
                            <p>{memory.size}</p>
                        </div>
                    ),
                },
                {
                    id: "serialNumber",
                    component: (
                        <div className="cluster normalGap limit">
                            <p>{memory.serialNumber}</p>
                        </div>
                    ),
                },
                {
                    id: "speed",
                    component: (
                        <div className="cluster normalGap limit">
                            <p>{memory.speed}</p>
                        </div>
                    ),
                },
            ],
            actionsComponents: [<></>],
            isDraggable: false,
        };
        table.rows.push(row);
    }

    return table;
};

export const convertProcessorInfoToTable = (hardwareInfo: BackendHardwareInfo): ITable => {
    const table: ITable = { id: "processorInfo", headers: [], rows: [] };

    table.headers = [
        { id: "summary", component: <>Processor Info</>, width: "25%", minWidth: 100 },
        { id: "value", component: "", width: "75%", minWidth: 100 },
    ];

    const processorRows: { id: string; label: string; value: React.ReactNode }[] = [
        { id: "vendor", label: "Vendor", value: hardwareInfo.processorInfo.vendor },
        { id: "coreCount", label: "Core Count", value: hardwareInfo.processorInfo.coreCount },
        { id: "coreEnabled", label: "Core Enabled", value: hardwareInfo.processorInfo.coreEnabled },
        { id: "version", label: "Version", value: hardwareInfo.processorInfo.version },
        { id: "threadCount", label: "Thread Count", value: hardwareInfo.processorInfo.threadCount },
        { id: "currentSpeed", label: "Current Speed", value: hardwareInfo.processorInfo.currentSpeed },
    ];

    for (const row of processorRows) {
        table.rows.push({
            id: row.id,
            columns: [
                {
                    id: "summary",
                    component: (
                        <div className="cluster normalGap limit">
                            <p>{row.label}</p>
                        </div>
                    ),
                },
                {
                    id: "value",
                    component: (
                        <div className="cluster normalGap limit">
                            <p>{row.value}</p>
                        </div>
                    ),
                },
            ],
            actionsComponents: [<></>],
            isDraggable: false,
        });
    }

    return table;
};

export const convertChassisInfoToTable = (hardwareInfo: BackendHardwareInfo): ITable => {
    const table: ITable = { id: "chassisInfo", headers: [], rows: [] };

    table.headers = [
        { id: "summary", component: <>Chassis Info</>, width: "25%", minWidth: 100 },
        { id: "value", component: "", width: "75%", minWidth: 100 },
    ];

    const chassisRows: { id: string; label: string; value: React.ReactNode }[] = [
        { id: "manufacturer", label: "Manufacturer", value: hardwareInfo.chassisInfo.manufacturer },
        { id: "assetTag", label: "Asset Tag", value: hardwareInfo.chassisInfo.assetTag },
        { id: "serialNumber", label: "Serial Number", value: hardwareInfo.chassisInfo.serialNumber },
    ];

    for (const row of chassisRows) {
        table.rows.push({
            id: row.id,
            columns: [
                {
                    id: "summary",
                    component: (
                        <div className="cluster normalGap limit">
                            <p>{row.label}</p>
                        </div>
                    ),
                },
                {
                    id: "value",
                    component: (
                        <div className="cluster normalGap limit">
                            <p>{row.value}</p>
                        </div>
                    ),
                },
            ],
            actionsComponents: [<></>],
            isDraggable: false,
        });
    }

    return table;
};

export const convertCaptureCardToTable = (captureCardInfo: BackendCaptureCardInfo): ITable => {
    const table: ITable = { id: "captureCard", headers: [], rows: [] };

    table.headers = [
        { id: "summary", component: <>Capture Card</>, width: "25%", minWidth: 100 },
        { id: "value", component: "", width: "75%", minWidth: 100 },
    ];

    const captureCardRows: { id: string; label: string; value: React.ReactNode }[] = [
        {
            id: "inputs",
            label: "Inputs",
            value: captureCardInfo.inputs.length > 0 ? captureCardInfo.inputs.length : "Not Detected",
        },
        {
            id: "ports",
            label: "Ports",
            value: captureCardInfo.ports.length > 0 ? captureCardInfo.ports.length : "Not Detected",
        },
    ];

    for (const row of captureCardRows) {
        table.rows.push({
            id: row.id,
            columns: [
                {
                    id: "summary",
                    component: (
                        <div className="cluster normalGap limit">
                            <p>{row.label}</p>
                        </div>
                    ),
                },
                {
                    id: "value",
                    component: (
                        <div className="cluster normalGap limit">
                            <p>{row.value}</p>
                        </div>
                    ),
                },
            ],
            actionsComponents: [<></>],
            isDraggable: false,
        });
    }

    return table;
};

export const convertBIOSInfoToTable = (hardwareInfo: BackendHardwareInfo): ITable => {
    const table: ITable = { id: "BIOSInfo", headers: [], rows: [] };

    table.headers = [
        { id: "summary", component: <>BIOS Info</>, width: "25%", minWidth: 100 },
        { id: "value", component: "", width: "75%", minWidth: 100 },
    ];

    const biosRows: { id: string; label: string; value: React.ReactNode }[] = [
        { id: "version", label: "Version", value: hardwareInfo.biosInfo.version },
        { id: "releaseDate", label: "Release Date", value: hardwareInfo.biosInfo.releaseDate },
        { id: "romSize", label: "ROM Size", value: hardwareInfo.biosInfo.romSize },
        { id: "vendor", label: "Vendor", value: hardwareInfo.biosInfo.vendor },
    ];

    for (const row of biosRows) {
        table.rows.push({
            id: row.id,
            columns: [
                {
                    id: "summary",
                    component: (
                        <div className="cluster normalGap limit">
                            <p>{row.label}</p>
                        </div>
                    ),
                },
                {
                    id: "value",
                    component: (
                        <div className="cluster normalGap limit">
                            <p>{row.value}</p>
                        </div>
                    ),
                },
            ],
            actionsComponents: [<></>],
            isDraggable: false,
        });
    }

    return table;
};
